import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import BASE_URL from "../../../assets/global/url";
import axios from "axios";
import PDFPreviewFrame from "../../../components/PDFPreviewFrame";
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 30,
  },
  header: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: "center",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableCol: {
    // width: "16.66666666666667%",
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableCell: {
    margin: "auto",
    marginTop: 5,
    fontSize: 10,
    textAlign: "center",
  },
  infoSection: {
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    marginBottom: 10,
  },
  column: {
    flex: 1,
    marginRight: 20,
  },
  label: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 4,
  },
  value: {
    fontSize: 12,
    padding: 8,
    borderBottom: "1px solid gray",
  },
  totalContainer: {
    marginTop: "20px",
    flexDirection: "row",
  },
  totalCard: {
    width: "50%",
    padding: "10px",
  },
  totalCardSecond: {
    width: "50%",
    padding: "10px",
  },
  totalCardSecondBetween: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  totalCardSecondBetweenTotalAmount: {
    backgroundColor: "#0A9CBC",
    borderRadius: "5px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 5,
    paddingLeft: 8,
    paddingRight: 8,
    marginTop: "5px",
  },
  labelSpecial: {
    fontSize: 10,
    color: "#fff",
    marginBottom: 4,
    fontWeight: 600,
  },
  tableHead: {
    backgroundColor: "#E9ECEF",
    padding: 5,
  },
  footer: {
    position: "absolute",
    top: 8,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 20,
  },
  footerText: {
    fontSize: 8,
    color: "#000",
  },
});
// Fetches the expense and renders the PDF to a blob URL exactly once per open.
// PDFViewer / PDFDownloadLink re-render the whole PDF (and reload the iframe)
// on every parent re-render, which makes the preview blink, so the caller
// shows the returned url with PDFPreviewFrame and a plain <a download>.
export const useExpensePDF = (id, enabled = true) => {
  const [state, setState] = useState({
    url: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !id) return;
    let cancelled = false;
    let createdUrl = null;

    setState({ url: null, loading: true, error: null });

    const load = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/expenses/pdfExpense`, {
          params: { id },
        });
        if (cancelled) return;

        const blob = await pdf(<PDFExpenses pdfData={res.data} />).toBlob();
        if (cancelled) return;

        createdUrl = URL.createObjectURL(blob);
        setState({ url: createdUrl, loading: false, error: null });
      } catch (error) {
        console.error(error);
        if (!cancelled) setState({ url: null, loading: false, error });
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      // Drop the revoked url so a reopened modal shows the spinner, not a
      // broken frame, until the new PDF is ready.
      setState({ url: null, loading: false, error: null });
    };
  }, [id, enabled]);

  return state;
};

export const ExpensePDFFrame = (props) => (
  <PDFPreviewFrame title="Expenses" {...props} />
);

// Pure document built from the expenses/pdfExpense response.
const PDFExpenses = ({ pdfData }) => {
  const today = new Date().toLocaleDateString();

  return (
    <Document title={`Expenses - ${pdfData?.transaction_id}`}>
      <Page size="A4" style={styles.page} orientation="landscape">
        <Text style={styles.header}>Expenses Overview</Text>

        <View style={styles.infoSection}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Transaction ID</Text>
              <Text style={styles.value}>{pdfData?.transaction_id}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Foreign Type</Text>
              <Text style={styles.value}>{pdfData?.foreign}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Expenses Type 1</Text>
              <Text style={styles.value}>
                {pdfData?.expenses2?.expenses_one.expenses_type_one}
              </Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.label}>Expenses Type 2</Text>
              <Text style={styles.value}>{pdfData?.expenses2?.sub_type}</Text>
            </View>
          </View>

          {pdfData?.product_name != null ? (
            <>
              <View style={styles.row}>
                <View style={styles.column}>
                  <Text style={styles.label}>Product Name</Text>
                  <Text style={styles.value}>
                    {pdfData?.product_name || ""}
                  </Text>
                </View>

                <View style={styles.column}>
                  <Text style={styles.label}>Unit Price</Text>
                  <Text style={styles.value}>{pdfData?.unitPrice || ""}</Text>
                </View>

                <View style={styles.column}>
                  <Text style={styles.label}>Quantity</Text>
                  <Text style={styles.value}>{pdfData?.assetQuantity}</Text>
                </View>
              </View>
            </>
          ) : null}

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Due Date</Text>
              <Text style={styles.value}>{pdfData?.due_date}</Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.label}>Expenses Date</Text>
              <Text style={styles.value}>{pdfData?.expenses_date}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Amount</Text>
              <Text style={styles.value}>
                {pdfData?.totalAmount} {pdfData?.currency?.currency_name}
              </Text>
            </View>

            {/* --- ADD THIS NEW COLUMN --- */}
            <View style={styles.column}>
              <Text style={styles.label}>Cheque Number</Text>
              <Text style={styles.value}>{pdfData?.expensesCheque || ""}</Text>
            </View>
            {/* --------------------------- */}

            <View style={styles.column}>
              <Text style={styles.label}>Description</Text>
              <Text style={styles.value}>{pdfData?.desc}</Text>
            </View>
          </View>

          {pdfData?.currency?.currency_name !== "PHP" ? (
            <>
              <View style={styles.row}>
                <View style={styles.column}>
                  <Text style={styles.label}>Rate</Text>
                  <Text style={styles.value}>{pdfData?.rate}</Text>
                </View>

                <View style={styles.column}></View>
              </View>
            </>
          ) : null}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{pdfData?.transaction_id}</Text>
          <Text style={styles.footerText}>{today}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFExpenses;
